import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { colors, spacing, shadows } from '../theme/index';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config/api';

function UserLogin() {
  const { login } = useAuth();
  const [step, setStep] = useState(1); // 1: phone input, 2: OTP verify
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => setTimer(timer - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!phone) {
      setError('Please enter phone number');
      setLoading(false);
      return;
    }

    if (phone.length !== 10) {
      setError('Phone number must be 10 digits');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/send-login-otp`, {
        phone: phone,
      });
      setSuccess('OTP sent to your phone number');
      setStep(2);
      setTimer(60);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!otp) {
      setError('Please enter OTP');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/verify-login-otp`, {
        phone: phone,
        otp: otp,
      });
      
      const { token, user } = response.data.data;
      login(token, user);
      setSuccess('Login successful!');
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      setError(error.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (timer > 0) return;
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/auth/send-login-otp`, {
        phone: phone,
      });
      setSuccess('OTP resent successfully');
      setTimer(60);
    } catch (error) {
      setError('Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${colors.primary[50]} 0%, ${colors.primary[100]} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '1rem',
          boxShadow: shadows.card,
          padding: spacing.xl,
          width: '100%',
          maxWidth: '420px',
        }}
      >
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.xs }}>
            Welcome Back
          </h1>
          <p style={{ fontSize: '0.95rem', color: colors.text.secondary, marginBottom: spacing.lg }}>
            Login using your phone number
          </p>
        </motion.div>

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            style={{
              backgroundColor: '#FEE2E2',
              border: `1px solid #FECACA`,
              color: '#DC2626',
              padding: `${spacing.md}`,
              borderRadius: spacing.md,
              marginBottom: spacing.lg,
              fontSize: '0.875rem',
            }}
          >
            {error}
          </motion.div>
        )}

        {/* Success Message */}
        {success && (
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            style={{
              backgroundColor: '#ECFDF5',
              border: `1px solid #A7F3D0`,
              color: '#059669',
              padding: `${spacing.md}`,
              borderRadius: spacing.md,
              marginBottom: spacing.lg,
              fontSize: '0.875rem',
            }}
          >
            {success}
          </motion.div>
        )}

        {/* Step 1: Phone Input */}
        {step === 1 ? (
          <form onSubmit={handleSendOTP} style={{ width: '100%' }}>
            <div style={{ marginBottom: spacing.lg }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: colors.text.primary,
                  marginBottom: spacing.sm,
                }}
              >
                Phone Number
              </label>
              <div style={{ display: 'flex', gap: 0 }}>
                <div
                  style={{
                    padding: `${spacing.md}`,
                    backgroundColor: colors.background.alt,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '0.5rem 0 0 0.5rem',
                    color: colors.text.primary,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  +91
                </div>
                <input
                  type="tel"
                  placeholder="9876543210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.slice(0, 10))}
                  maxLength="10"
                  required
                  style={{
                    flex: 1,
                    padding: `${spacing.md}`,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '0 0.5rem 0.5rem 0',
                    borderLeft: 'none',
                    fontSize: '1rem',
                    color: colors.text.primary,
                    outline: 'none',
                    transition: 'all 0.2s ease',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = colors.primary[600];
                    e.target.style.boxShadow = `0 0 0 3px rgba(61, 139, 139, 0.1)`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = colors.border;
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              <p style={{ fontSize: '0.75rem', color: colors.text.tertiary, marginTop: spacing.xs }}>
                Enter 10-digit mobile number
              </p>
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={loading}
              type="submit"
              style={{
                width: '100%',
                padding: `${spacing.md} ${spacing.lg}`,
                backgroundColor: colors.primary[600],
                color: 'white',
                border: 'none',
                borderRadius: spacing.md,
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                transition: 'all 0.2s ease',
                marginBottom: spacing.lg,
              }}
              onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = colors.primary[700])}
              onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = colors.primary[600])}
            >
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </motion.button>
          </form>
        ) : (
          /* Step 2: OTP Verification */
          <form onSubmit={handleVerifyOTP} style={{ width: '100%' }}>
            <div
              style={{
                backgroundColor: colors.primary[50],
                border: `1px solid ${colors.primary[200]}`,
                borderRadius: spacing.md,
                padding: spacing.lg,
                marginBottom: spacing.lg,
              }}
            >
              <p style={{ fontSize: '0.875rem', color: colors.text.secondary, marginBottom: spacing.xs }}>
                OTP sent to
              </p>
              <p style={{ fontSize: '1.125rem', fontWeight: 600, color: colors.text.primary }}>
                +91 {phone}
              </p>
            </div>

            <div style={{ marginBottom: spacing.lg }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: colors.text.primary,
                  marginBottom: spacing.sm,
                }}
              >
                Enter OTP
              </label>
              <input
                type="tel"
                placeholder="000000"
                value={otp}
                onChange={(e) => setOtp(e.target.value.slice(0, 6))}
                maxLength="6"
                required
                style={{
                  width: '100%',
                  padding: `${spacing.md}`,
                  border: `2px solid ${colors.border}`,
                  borderRadius: spacing.md,
                  fontSize: '1.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.2em',
                  textAlign: 'center',
                  color: colors.text.primary,
                  outline: 'none',
                  transition: 'all 0.2s ease',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = colors.primary[600];
                  e.target.style.boxShadow = `0 0 0 3px rgba(61, 139, 139, 0.1)`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = colors.border;
                  e.target.style.boxShadow = 'none';
                }}
              />
              <p style={{ fontSize: '0.75rem', color: colors.text.tertiary, marginTop: spacing.xs }}>
                6-digit OTP code
              </p>
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={loading}
              type="submit"
              style={{
                width: '100%',
                padding: `${spacing.md} ${spacing.lg}`,
                backgroundColor: colors.primary[600],
                color: 'white',
                border: 'none',
                borderRadius: spacing.md,
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                transition: 'all 0.2s ease',
                marginBottom: spacing.lg,
              }}
              onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = colors.primary[700])}
              onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = colors.primary[600])}
            >
              {loading ? 'Verifying...' : 'Login'}
            </motion.button>

            {/* Resend OTP */}
            <div style={{ textAlign: 'center', marginBottom: spacing.lg }}>
              <p style={{ fontSize: '0.875rem', color: colors.text.secondary, marginBottom: spacing.sm }}>
                Didn't receive OTP?
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                disabled={timer > 0 || loading}
                onClick={handleResendOTP}
                type="button"
                style={{
                  backgroundColor: 'transparent',
                  color: colors.primary[600],
                  border: 'none',
                  fontWeight: 600,
                  cursor: timer > 0 || loading ? 'not-allowed' : 'pointer',
                  opacity: timer > 0 ? 0.6 : 1,
                  transition: 'all 0.2s ease',
                  fontSize: '0.9rem',
                }}
              >
                {timer > 0 ? `Resend in ${timer}s` : 'Resend OTP'}
              </motion.button>
            </div>

            {/* Change Phone Button */}
            <motion.button
              whileHover={{ x: -2 }}
              onClick={() => setStep(1)}
              type="button"
              style={{
                width: '100%',
                padding: `${spacing.sm} ${spacing.md}`,
                color: colors.primary[600],
                backgroundColor: 'transparent',
                border: `1px solid ${colors.primary[200]}`,
                borderRadius: spacing.md,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = colors.primary[50];
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              ← Change Phone Number
            </motion.button>
          </form>
        )}

        {/* Sign Up Link */}
        <p
          style={{
            textAlign: 'center',
            color: colors.text.secondary,
            marginTop: spacing.lg,
            fontSize: '0.9rem',
          }}
        >
          Don't have an account?{' '}
          <Link
            to="/user-register"
            style={{
              color: colors.primary[600],
              fontWeight: 600,
              textDecoration: 'none',
              transition: 'color 0.2s ease',
            }}
            onMouseEnter={(e) => (e.target.style.color = colors.primary[700])}
            onMouseLeave={(e) => (e.target.style.color = colors.primary[600])}
          >
            Sign up
          </Link>
        </p>
      </motion.div>
    </motion.div>
  );
}

export default UserLogin;
